import { NgClass, NgIf, NgStyle } from '@angular/common';
import { Component, Inject, OnInit, TemplateRef } from '@angular/core';

import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

import { TooltipService } from '@babybeet/angular-tooltip';
import { CanvasJson } from '@lohmann-birkner/luic/lib/components/luic-painting-tool/painting-tool.component';
import { TranslateModule } from '@ngx-translate/core';
import * as fabric from 'fabric';
import { Canvas, FabricObject } from 'fabric';
import * as pdfjsLib from 'pdfjs-dist';

import { ToolboxService } from '../../../../services/toolbox.service';
import { FindingItem } from '../../med-view.component';
import { CanvasRendererService } from '../../services/canvas-renderer.service';

export interface ExtendedCanvasJson extends CanvasJson {
  originalWidth: number;
  originalHeight: number;
}

@Component({
  selector: 'app-image-doc-drawer',
  templateUrl: './image-doc-drawer.component.html',
  styleUrls: ['./image-doc-drawer.component.scss'],
  standalone: true,
  providers: [CanvasRendererService],
  imports: [
    MatIcon,
    MatIconButton,
    TranslateModule,
    MatDivider,
    MatButton,
    MatMiniFabButton,
    NgIf,
    MatTooltip,
    NgClass,
    NgStyle,
    MatProgressSpinner,
  ],
})
export class ImageDocDrawerComponent implements OnInit {
  public isDrawingMode = false;
  public isMarkingMode = false;
  public fabricSettings: any = {
    color: 'black',
    width: 4,
  };
  public areAnnotationsVisible = true;
  public isCanvasInitialized: boolean = false;
  public selectedFinding: FindingItem<any> = {} as FindingItem<any>;
  public canvasElementId: string = '';
  private fabricCanvas!: Canvas;
  private isColorPaletteTooltipOpen: boolean = false;
  private isStrokeWidthTooltipOpen: boolean = false;
  private textToolMouseHandler: ((event: fabric.TEvent) => void) | null = null;

  constructor(
    public dialogRef: MatDialogRef<ImageDocDrawerComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      canvasElementId: string;
      selectedFinding: FindingItem<any>;
    },
    public canvasRendererService: CanvasRendererService,
    private readonly tooltipService: TooltipService,
    private toolbox: ToolboxService,
  ) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdf.worker.mjs';

    this.selectedFinding = data.selectedFinding;
    this.canvasElementId = data.canvasElementId;
  }

  async ngOnInit(): Promise<void> {
    const fabricCanvas = await this.canvasRendererService.initCanvas(
      'myCanvas',
      'PaintingToolWrapper',
      this.selectedFinding,
      this.selectedFinding.data.annotations,
      true,
    );

    if (fabricCanvas) {
      this.fabricCanvas = fabricCanvas;
    }
  }

  public enableTextAdding(): void {
    this.deselectAllTools();

    const onMouseDown = (event: fabric.TEvent) => {
      event.e.preventDefault();
      event.e.stopPropagation();

      const pointer = this.fabricCanvas.getPointer(event.e);
      const x = pointer.x;
      const y = pointer.y;

      const text = new fabric.IText('TEXT', {
        left: x,
        top: y,
        fontSize: 24,
        fill: this.fabricSettings.color || 'black',
        fontFamily: 'Arial',
        editable: true,
      });

      this.fabricCanvas.add(text);
      this.fabricCanvas.setActiveObject(text);

      text.enterEditing();
      text.hiddenTextarea?.focus();

      this.fabricCanvas.renderAll();

      this.fabricCanvas.off('mouse:down', onMouseDown);
      this.textToolMouseHandler = null;
    };

    this.textToolMouseHandler = onMouseDown;
    this.fabricCanvas.on('mouse:down', onMouseDown);
  }

  public toggleDrawingMode(): void {
    this.deselectAllTools();

    this.isDrawingMode = !this.isDrawingMode;
    this.fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(this.fabricCanvas);
    this.fabricCanvas.isDrawingMode = this.isDrawingMode;

    if (this.isDrawingMode && this.fabricCanvas.freeDrawingBrush) {
      this.updateColor(this.fabricSettings.color);
      this.updateStrokeWitdh(this.fabricSettings.width);
    }
  }

  public updateStrokeWitdh(
    width: number,
    event?: MouseEvent,
    templateRefContent?: TemplateRef<any>,
  ) {
    this.fabricSettings.width = width;

    if ((this.isDrawingMode || this.isMarkingMode) && this.fabricCanvas.freeDrawingBrush) {
      this.fabricCanvas.freeDrawingBrush.width = this.fabricSettings.width;
    }

    if (event && templateRefContent) {
      this.toggleTooltip(event, templateRefContent, 'strokeWidth');
    }
  }

  public updateColor(color: string, event?: MouseEvent, templateRefContent?: TemplateRef<any>) {
    this.fabricSettings.color = color;

    if ((this.isDrawingMode || this.isMarkingMode) && this.fabricCanvas.freeDrawingBrush) {
      this.fabricCanvas.freeDrawingBrush.color = this.convertToRgba(color, 0.5);
    }

    const activeObjects: FabricObject[] = this.fabricCanvas.getActiveObjects();
    if (activeObjects.length) {
      activeObjects.forEach((activeObject: FabricObject) => {
        switch (activeObject.type) {
          case 'rect':
          case 'circle':
          case 'triangle':
          case 'path':
            if ('stroke' in activeObject) {
              activeObject.set('stroke', this.fabricSettings.color);
            }
            break;

          case 'text':
          case 'i-text':
            if ('fill' in activeObject) {
              activeObject.set('fill', this.fabricSettings.color);
            }
            break;

          default:
            console.warn(`Farbänderung für Objekttyp '${activeObject.type}' nicht implementiert.`);
        }
      });

      this.fabricCanvas.renderAll();
    }

    if (event && templateRefContent) {
      this.toggleTooltip(event, templateRefContent, 'colorPalette');
    }
  }

  public toggleMarkingMode(): void {
    this.deselectAllTools();

    this.isMarkingMode = !this.isMarkingMode;
    this.fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(this.fabricCanvas);
    this.fabricCanvas.isDrawingMode = this.isMarkingMode;

    if (this.isMarkingMode && this.fabricCanvas.freeDrawingBrush) {
      const brush = this.fabricCanvas.freeDrawingBrush;

      brush.color = 'rgba(255, 0, 0, 0.4)';
      brush.width = 12;
      brush.strokeLineCap = 'round';
    }
  }

  public addRect() {
    this.deselectAllTools();

    const rectangle = new fabric.Rect({
      left: 100,
      top: 100,
      width: 200,
      height: 200,
      opacity: 1,
      fill: 'transparent',
      stroke: this.fabricSettings.color,
      strokeWidth: this.fabricSettings.width,
    });

    this.fabricCanvas.add(rectangle);
  }

  public addCircle() {
    this.deselectAllTools();

    const circle = new fabric.Circle({
      left: 150,
      top: 150,
      radius: 50,
      opacity: 1,
      fill: 'transparent',
      stroke: this.fabricSettings.color,
      strokeWidth: this.fabricSettings.width,
    });

    this.fabricCanvas.add(circle);
  }

  public addArrow(): void {
    this.deselectAllTools();

    const arrow1Path = new fabric.Path(`M -100 0 L 100 0 L 70 -15 M 100 0 L 70 15`, {
      stroke: this.fabricSettings.color,
      opacity: 1,
      strokeWidth: this.fabricSettings.width,
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      fill: 'transparent',
      left: 10,
      top: 40,
    });

    this.fabricCanvas.add(arrow1Path);
  }

  public onClickOnCancel() {
    this.dialogRef.close({ role: 'cancel', data: {} });
  }

  public toggleTooltip(
    event: MouseEvent,
    templateRefContent: TemplateRef<any>,
    tooltipType: 'colorPalette' | 'strokeWidth',
  ): void {
    const isCurrentlyOpen =
      (tooltipType === 'colorPalette' && this.isColorPaletteTooltipOpen) ||
      (tooltipType === 'strokeWidth' && this.isStrokeWidthTooltipOpen);

    this.hideTooltip();

    if (!isCurrentlyOpen) {
      this.showTooltip(event, templateRefContent);

      if (tooltipType === 'colorPalette') {
        this.isColorPaletteTooltipOpen = true;
      } else if (tooltipType === 'strokeWidth') {
        this.isStrokeWidthTooltipOpen = true;
      }
    }
  }

  public deleteSelectedObject() {
    const activeObject = this.fabricCanvas.getActiveObject();

    if (activeObject) {
      this.fabricCanvas.remove(activeObject);
      this.fabricCanvas.renderAll();
    }
  }

  public toggleObjectVisibility(): void {
    this.areAnnotationsVisible = !this.areAnnotationsVisible;

    this.fabricCanvas.getObjects().forEach(obj => {
      if (obj.type !== 'image' || obj.selectable) {
        obj.visible = this.areAnnotationsVisible;
      }
    });

    this.fabricCanvas.renderAll();
  }

  public onClickOnSave(): void {
    const canvasWidth = this.fabricCanvas.getWidth();
    const canvasHeight = this.fabricCanvas.getHeight();

    const annotations: ExtendedCanvasJson = {
      version: fabric.version,
      objects: this.fabricCanvas
        .getObjects()
        .filter(obj => obj.type !== 'image') // Bilder nicht speichern
        .map(obj => {
          const relativeObject = obj.toObject() as FabricObject;

          // Relative Position und Größe berechnen
          relativeObject.left = (obj.left || 0) / canvasWidth;
          relativeObject.top = (obj.top || 0) / canvasHeight;

          if (
            obj.type === 'rect' ||
            obj.type === 'circle' ||
            obj.type === 'path' ||
            obj.type === 'itext'
          ) {
            if (relativeObject.width) {
              relativeObject.width = (relativeObject.width * (obj.scaleX || 1)) / canvasWidth;
            }
            if (relativeObject.height) {
              relativeObject.height = (relativeObject.height * (obj.scaleY || 1)) / canvasHeight;
            }
          }

          return relativeObject;
        }),
      originalWidth: canvasWidth,
      originalHeight: canvasHeight,
    };

    this.dialogRef.close({ role: 'save', data: { annotations } });
  }

  private hideTooltip(): void {
    this.tooltipService.hide();
    this.isColorPaletteTooltipOpen = false;
    this.isStrokeWidthTooltipOpen = false;
  }

  private showTooltip(event: MouseEvent, templateRefContent: TemplateRef<any>): void {
    const target = event.target as Element | null;
    if (target) {
      this.tooltipService.show(target, {
        content: templateRefContent,
        context: { $implicit: 'TemplateRef' },
        placement: 'horizontal',
        theme: 'light',
      });
    }
  }

  private deselectAllTools(): void {
    this.isDrawingMode = false;
    this.isMarkingMode = false;
    this.fabricCanvas.isDrawingMode = false;

    if (this.textToolMouseHandler) {
      this.fabricCanvas.off('mouse:down', this.textToolMouseHandler);
      this.textToolMouseHandler = null;
    }
  }
  private convertToRgba(hex: string, opacity: number): string {
    if (hex.startsWith('#')) {
      hex = hex.slice(1);
    }

    let r, g, b;

    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      console.warn('Invalid hex color:', hex);
      return 'rgba(0, 0, 0, 0.5)';
    }

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
}
